import React from "react";
import { useObserver } from "mobx-react";
import cns from "classnames";
import moment from "moment";
import { isEmpty } from "lodash";
import { Checkbox, IconButton } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AccountGroup, DeleteCircle, PencilCircle } from "mdi-material-ui";
import { CustomerContactPersonFragment } from "@graphql";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import { Body, CustomerGroupChip, PlainLink } from "@components";
import { hl } from "@components/Highlighter";

type Item = CustomerContactPersonFragment;

type Props = {
  canAdd?: boolean;
  canEdit?: boolean;
  item: Item;
  onAdd?: (e: React.SyntheticEvent<any>, roles: string[]) => void;
  onClick?: () => void;
  onEdit?: (id: string, e: React.SyntheticEvent<any>) => void;
  onDelete?: () => void;
  onSelect?: (id: string, e: React.SyntheticEvent<any>) => void;
  search?: string;
  selected?: boolean;
  hightlightImportant?: boolean;
  withCheckbox?: boolean;
  style?: React.CSSProperties;
};

function CustomerContactCard(props: Props) {
  const { canEdit, onEdit, onSelect, search, item, selected, onDelete } = props;
  const canDelete = store().user.can("customer-contact-person:delete:all");
  const classes = useStyles(props);

  const isImportant = item.Roles.some(store().cache.isStaffingRole);
  const name = item.Name;
  const isStaffingRole = item.Roles.some(store().cache.isStaffingRole);

  const className = cns(classes.root, {
    [classes.highlighted]: isImportant && props.hightlightImportant,
    [classes.clickable]: !!props.onClick
  });

  return useObserver(() => (
    <div
      className={className}
      style={props.style}
      onClick={(e) => {
        onSelect && onSelect(item._id, e);
        props.onClick && props.onClick();
      }}
    >
      {props.withCheckbox && (
        <div className={classes.checkBox}>
          {<Checkbox checked={selected} className={classes.checkBoxIcon} />}
        </div>
      )}

      <div className={classes.flexCol}>
        <div className={classes.nameCell}>
          {isImportant && isStaffingRole && <AccountGroup />}

          <div className={classes.list}>
            <div className={classes.name}>{hl(name, search)}</div>

            {item.Title && (
              <div>
                <i>{hl(item.Title, search)}</i>
              </div>
            )}
          </div>
        </div>

        {!isEmpty(item.Roles) && (
          <div className={classes.list}>
            <Body style={{ fontSize: "0.875rem" }}>{locale.Roles}: &nbsp;</Body>

            {item.Roles?.map((role) => {
              if (store().cache.isStaffingRole(role)) {
                return (
                  <div className={classes.roleItem} key={role}>
                    <AccountGroup className={classes.accountGroup} />
                    <Body>{role}</Body>
                  </div>
                );
              }

              return <Body key={role}>{role}</Body>;
            })}
          </div>
        )}

        {item.AreasOfExpertise.length > 0 && (
          <div className={classes.list}>
            <Body style={{ fontSize: "0.875rem" }}>{locale.AreaOfExpertise}: &nbsp;</Body>

            {item.AreasOfExpertise.map((x) => (
              <Body key={x}>{x}</Body>
            ))}
          </div>
        )}

        {item.BooksProfessions.length > 0 && (
          <div className={classes.list}>
            <Body style={{ fontSize: "0.875rem" }}>
              {locale.BooksProfessions}: &nbsp;
            </Body>

            {item.BooksProfessions.map((x) => (
              <Body key={x}>{x}</Body>
            ))}
          </div>
        )}

        <div style={{ display: "flex" }}>
          {item.Phone && (
            <div className={classes.phone}>
              <PlainLink
                inline
                href={`tel:${item.Phone}`}
                onClick={(e) => e.stopPropagation()}
                withIcon
              >
                {hl(item.Phone, search)}
              </PlainLink>
            </div>
          )}{" "}
          {item.CellPhone && (
            <div className={classes.phone}>
              <PlainLink
                inline
                href={`mobilnum:${item.CellPhone}`}
                onClick={(e) => e.stopPropagation()}
                withIcon
              >
                {hl(item.CellPhone, search)}
              </PlainLink>
            </div>
          )}
          <div className={classes.email}>
            <PlainLink
              inline
              href={`mailto:${item.Email}`}
              onClick={(e) => e.stopPropagation()}
              withIcon
            >
              {hl(item.Email, search)}
            </PlainLink>
          </div>
        </div>

        {item.LastChange && (
          <Body className={classes.lastChange} data-test="LastChange">
            {formatLastChange(item.LastChange)}
          </Body>
        )}
      </div>

      <div className={classes.rowControls}>
        {item.CustomerGroups?.map((x) => (
          <CustomerGroupChip key={x}>{x}</CustomerGroupChip>
        ))}

        {canEdit && (
          <IconButton
            color="inherit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item._id, e);
            }}
            data-test="EditBtn"
            style={{ padding: 4 }}
          >
            <PencilCircle fontSize="small" />
          </IconButton>
        )}

        {onDelete && (
          <IconButton
            color="inherit"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            data-test="DeleteBtn"
            style={{ padding: 4 }}
            disabled={!onDelete || !canDelete}
          >
            <DeleteCircle fontSize="small" />
          </IconButton>
        )}
      </div>
    </div>
  ));
}

function formatLastChange(change: Item["LastChange"]) {
  const label = change.IsUpdate ? locale.Updated : locale.Created;
  const date = moment(change.Date).format("YYYY-MM-DD");
  const by = change.User?.Name ? ` ${locale.by} ${change.User.Name}` : "";

  return `${label} ${date}${by}`;
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    fontFamily: theme.typography.fontFamily,
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    padding: theme.spacing(),
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
    transition: theme.transitions.create("background-color"),

    "&:hover": {
      backgroundColor: theme.palette.secondary.background
    }
  },

  clickable: {
    "&:hover": { cursor: "pointer" }
  },

  nameCell: {
    display: "flex",

    "& > svg": {
      marginRight: theme.spacing(1 / 2)
    }
  },

  list: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    "& > *:first-child": { fontWeight: "bold" },
    "& > *:not(:first-child):not(:last-child):after": {
      content: "','",
      display: "inline-block",
      marginRight: 4
    }
  },

  rowControls: {
    alignItems: "center",
    display: "flex",
    marginBottom: "auto"
  },

  addBtn: {
    position: "absolute",
    top: 6,
    right: 20
  },

  highlighted: {
    backgroundColor: theme.palette.secondary.background
  },

  error: {
    backgroundColor: theme.palette.error.background
  },

  cellNarrow: {
    alignItems: "center",
    display: "flex"
  },

  checkBox: {
    display: "flex",
    alignItems: "flex-start"
  },

  checkBoxIcon: {
    paddingTop: 0,
    paddingLeft: 5
  },

  flexCol: {
    display: "flex",
    flexDirection: "column",
    flex: 1
  },

  flexRow: {
    display: "flex",
    flexDirection: "row"
  },

  roleItem: {
    display: "inline-flex",
    flexDirection: "row",
    whiteSpace: "nowrap",
    "& svg": {
      marginRight: 4
    }
  },

  name: {
    marginRight: 10,
    paddingTop: 5,
    fontWeight: "bold"
  },

  phone: {
    marginRight: 10,
    color: theme.palette.secondary.main
  },

  email: {
    color: theme.palette.secondary.main
  },

  lastChange: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1 / 2)
  },

  accountGroup: {
    height: 18,
    width: 18
  },

  fauxRow: {},
  empty: {},
  title: {},
  addNewBtn: {},
  addNewContact: {}
}));

export default CustomerContactCard;
